import {ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState} from 'react';
import {socialFeedApi} from '../../../../services/socialFeedApi';
import {
  MAX_REEL_VIDEO_SIZE_BYTES,
  SUPPORTED_REEL_VIDEO_TYPES,
} from '../constants';
import {
  createVideoThumbnailPreviewUrl,
  isRequestCanceled,
  readVideoDurationFromPreviewUrl,
} from '../utils';

export const useUploadReelComposer = (
  token: string | null,
  refreshReels: () => Promise<void>,
  registerPendingReelThumbnail: (reelId: string, previewImageUrl: string) => void,
) => {
  const uploadReelRequestControllerRef = useRef<AbortController | null>(null);
  const uploadReelTitleInputRef = useRef<HTMLInputElement>(null);
  const uploadReelFileInputRef = useRef<HTMLInputElement>(null);
  const activeUploadSessionIdRef = useRef<string | null>(null);

  const [isUploadReelModalOpen, setIsUploadReelModalOpen] = useState(false);
  const [newReelTitle, setNewReelTitle] = useState('');
  const [newReelDescription, setNewReelDescription] = useState('');
  const [newReelIsFeatured, setNewReelIsFeatured] = useState(false);
  const [selectedReelVideoFile, setSelectedReelVideoFile] = useState<File | null>(null);
  const [selectedReelVideoPreviewUrl, setSelectedReelVideoPreviewUrl] = useState('');
  const [selectedReelVideoDurationSeconds, setSelectedReelVideoDurationSeconds] = useState<number | undefined>();
  const [uploadReelMetadataLoading, setUploadReelMetadataLoading] = useState(false);
  const [uploadReelLoading, setUploadReelLoading] = useState(false);
  const [uploadReelError, setUploadReelError] = useState<string | null>(null);
  const [uploadReelValidationError, setUploadReelValidationError] = useState<string | null>(null);
  const [uploadReelStatusMessage, setUploadReelStatusMessage] = useState<string | null>(null);
  const [uploadReelProgressPercent, setUploadReelProgressPercent] = useState<number | null>(null);

  const abortPendingUploadSession = useCallback(async (uploadSessionId: string | null) => {
    if (!token || !uploadSessionId) {
      return;
    }

    try {
      await socialFeedApi.abortMediaUploadSession(token, uploadSessionId);
    } catch (cleanupError) {
      console.error('Failed to abort pending reel upload session:', cleanupError);
    } finally {
      if (activeUploadSessionIdRef.current === uploadSessionId) {
        activeUploadSessionIdRef.current = null;
      }
    }
  }, [token]);

  const clearSelectedReelVideo = useCallback(() => {
    setSelectedReelVideoFile(null);
    setSelectedReelVideoDurationSeconds(undefined);
    setUploadReelMetadataLoading(false);
    if (uploadReelFileInputRef.current) {
      uploadReelFileInputRef.current.value = '';
    }
  }, []);

  const resetComposerState = useCallback(() => {
    setNewReelTitle('');
    setNewReelDescription('');
    setNewReelIsFeatured(false);
    clearSelectedReelVideo();
    setUploadReelLoading(false);
    setUploadReelError(null);
    setUploadReelValidationError(null);
    setUploadReelStatusMessage(null);
    setUploadReelProgressPercent(null);
  }, [clearSelectedReelVideo]);

  const closeUploadReelModal = useCallback(() => {
    const activeUploadSessionId = activeUploadSessionIdRef.current;

    uploadReelRequestControllerRef.current?.abort();
    uploadReelRequestControllerRef.current = null;
    activeUploadSessionIdRef.current = null;
    setIsUploadReelModalOpen(false);
    resetComposerState();

    if (activeUploadSessionId) {
      void abortPendingUploadSession(activeUploadSessionId);
    }
  }, [abortPendingUploadSession, resetComposerState]);

  const openUploadReelModal = useCallback(() => {
    setIsUploadReelModalOpen(true);
    resetComposerState();
  }, [resetComposerState]);

  const openUploadReelFileBrowser = useCallback(() => {
    uploadReelFileInputRef.current?.click();
  }, []);

  const handleSelectedReelVideoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!SUPPORTED_REEL_VIDEO_TYPES.includes(selectedFile.type)) {
      clearSelectedReelVideo();
      setUploadReelError(null);
      setUploadReelValidationError('Only MP4, MOV, and AVI videos are supported.');
      return;
    }

    if (selectedFile.size > MAX_REEL_VIDEO_SIZE_BYTES) {
      clearSelectedReelVideo();
      setUploadReelError(null);
      setUploadReelValidationError('Video size must be 500 MB or less.');
      return;
    }

    setSelectedReelVideoFile(selectedFile);
    setUploadReelError(null);
    setUploadReelValidationError(null);
  }, [clearSelectedReelVideo]);

  const handleUploadReelSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setUploadReelError('You must be signed in to upload a reel.');
      return;
    }

    const trimmedTitle = newReelTitle.trim();
    const trimmedDescription = newReelDescription.trim();

    if (!trimmedTitle) {
      setUploadReelValidationError('Reel title is required.');
      setUploadReelError(null);
      return;
    }

    if (trimmedTitle.length > 100) {
      setUploadReelValidationError('Reel title must be 100 characters or less.');
      setUploadReelError(null);
      return;
    }

    if (trimmedDescription.length > 500) {
      setUploadReelValidationError('Reel description must be 500 characters or less.');
      setUploadReelError(null);
      return;
    }

    if (!selectedReelVideoFile) {
      setUploadReelValidationError('Please choose a video file to upload.');
      setUploadReelError(null);
      return;
    }

    if (uploadReelMetadataLoading) {
      setUploadReelValidationError('Please wait while we prepare your reel preview.');
      setUploadReelError(null);
      return;
    }

    uploadReelRequestControllerRef.current?.abort();
    const controller = new AbortController();
    uploadReelRequestControllerRef.current = controller;
    const requestSignal = controller.signal;
    let uploadSessionId: string | null = null;
    let localThumbnailPreviewUrl: string | null = null;

    try {
      setUploadReelLoading(true);
      setUploadReelError(null);
      setUploadReelValidationError(null);
      setUploadReelStatusMessage('Starting secure upload...');
      setUploadReelProgressPercent(0);

      const uploadSessionData = await socialFeedApi.createMediaUploadSession(token, {
        purpose: 'reel-video',
        fileName: selectedReelVideoFile.name,
        contentType: selectedReelVideoFile.type,
        fileSize: selectedReelVideoFile.size,
      }, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      uploadSessionId = uploadSessionData.uploadSession.id;
      activeUploadSessionIdRef.current = uploadSessionId;
      setUploadReelStatusMessage('Uploading reel to secure storage...');

      const completedParts = await socialFeedApi.uploadMultipartSessionFile(token, uploadSessionData, selectedReelVideoFile, {
        signal: requestSignal,
        onProgress: (fraction) => {
          setUploadReelProgressPercent(Math.round(fraction * 100));
        },
      });

      if (requestSignal.aborted) {
        return;
      }

      setUploadReelStatusMessage('Finalizing reel...');
      setUploadReelProgressPercent(100);

      const createdReelResponse = await socialFeedApi.createHighlightReel(token, {
        uploadSessionId,
        parts: completedParts,
        title: trimmedTitle,
        description: trimmedDescription || undefined,
        isFeatured: newReelIsFeatured,
        durationSeconds: selectedReelVideoDurationSeconds,
      }, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      try {
        if (selectedReelVideoPreviewUrl) {
          localThumbnailPreviewUrl = await createVideoThumbnailPreviewUrl(selectedReelVideoPreviewUrl);
        }
      } catch (thumbnailPreviewError) {
        console.error('Failed to prepare a local reel thumbnail preview:', thumbnailPreviewError);
      }

      if (requestSignal.aborted) {
        return;
      }

      if (createdReelResponse.reel._id && localThumbnailPreviewUrl) {
        registerPendingReelThumbnail(createdReelResponse.reel._id, localThumbnailPreviewUrl);
        localThumbnailPreviewUrl = null;
      }

      activeUploadSessionIdRef.current = null;
      setIsUploadReelModalOpen(false);
      resetComposerState();
      await refreshReels();
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      await abortPendingUploadSession(uploadSessionId);
      setUploadReelError(error instanceof Error ? error.message : 'Failed to upload reel');
      setUploadReelStatusMessage(null);
      setUploadReelProgressPercent(null);
    } finally {
      if (!requestSignal.aborted) {
        setUploadReelLoading(false);
      }

      if (uploadReelRequestControllerRef.current?.signal === requestSignal) {
        uploadReelRequestControllerRef.current = null;
      }

      if (localThumbnailPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(localThumbnailPreviewUrl);
      }
    }
  }, [
    abortPendingUploadSession,
    newReelDescription,
    newReelIsFeatured,
    newReelTitle,
    registerPendingReelThumbnail,
    refreshReels,
    resetComposerState,
    selectedReelVideoDurationSeconds,
    selectedReelVideoFile,
    selectedReelVideoPreviewUrl,
    token,
    uploadReelMetadataLoading,
  ]);

  useEffect(() => {
    if (!selectedReelVideoFile) {
      setSelectedReelVideoPreviewUrl('');
      setSelectedReelVideoDurationSeconds(undefined);
      setUploadReelMetadataLoading(false);
      return;
    }

    const previewUrl = URL.createObjectURL(selectedReelVideoFile);
    let isActive = true;

    setSelectedReelVideoPreviewUrl(previewUrl);
    setSelectedReelVideoDurationSeconds(undefined);
    setUploadReelMetadataLoading(true);

    void readVideoDurationFromPreviewUrl(previewUrl)
      .then((durationSeconds) => {
        if (!isActive) {
          return;
        }

        setSelectedReelVideoDurationSeconds(durationSeconds);
      })
      .finally(() => {
        if (isActive) {
          setUploadReelMetadataLoading(false);
        }
      });

    return () => {
      isActive = false;
      URL.revokeObjectURL(previewUrl);
    };
  }, [selectedReelVideoFile]);

  useEffect(() => {
    if (!isUploadReelModalOpen) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      uploadReelTitleInputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [isUploadReelModalOpen]);

  useEffect(() => () => {
    uploadReelRequestControllerRef.current?.abort();
    const activeUploadSessionId = activeUploadSessionIdRef.current;
    activeUploadSessionIdRef.current = null;

    if (activeUploadSessionId) {
      void abortPendingUploadSession(activeUploadSessionId);
    }
  }, [abortPendingUploadSession]);

  return {
    isUploadReelModalOpen,
    newReelTitle,
    newReelDescription,
    newReelIsFeatured,
    selectedReelVideoFile,
    selectedReelVideoPreviewUrl,
    selectedReelVideoDurationSeconds,
    uploadReelMetadataLoading,
    uploadReelLoading,
    uploadReelError,
    uploadReelValidationError,
    uploadReelStatusMessage,
    uploadReelProgressPercent,
    uploadReelTitleInputRef,
    uploadReelFileInputRef,
    setNewReelTitle,
    setNewReelDescription,
    setNewReelIsFeatured,
    clearSelectedReelVideo,
    closeUploadReelModal,
    openUploadReelModal,
    openUploadReelFileBrowser,
    handleSelectedReelVideoChange,
    handleUploadReelSubmit,
  };
};
